import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { api, unwrapObject } from "@/lib/api";

export interface DateRange {
  from: string;
  to: string;
}

export type RangePreset = "today" | "yesterday" | "this_week" | "last_week" | "this_month" | "last_month" | "this_quarter" | "last_90" | "this_year" | "custom";

const PRESET_LABEL: Record<RangePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_90: "Last 90 days",
  this_year: "This year",
  custom: "Custom range",
};

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function presetRange(preset: RangePreset): DateRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  // Weeks start on Monday
  const monday = new Date(y, m, now.getDate() - ((now.getDay() + 6) % 7));
  switch (preset) {
    case "today":
      return { from: ymd(now), to: ymd(now) };
    case "yesterday": {
      const d = new Date(y, m, now.getDate() - 1);
      return { from: ymd(d), to: ymd(d) };
    }
    case "this_week":
      return { from: ymd(monday), to: ymd(now) };
    case "last_week":
      return { from: ymd(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7)), to: ymd(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 1)) };
    case "last_month":
      return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    case "this_quarter":
      return { from: ymd(new Date(y, Math.floor(m / 3) * 3, 1)), to: ymd(now) };
    case "last_90":
      return { from: ymd(new Date(y, m, now.getDate() - 89)), to: ymd(now) };
    case "this_year":
      return { from: ymd(new Date(y, 0, 1)), to: ymd(now) };
    case "this_month":
    default:
      return { from: ymd(new Date(y, m, 1)), to: ymd(now) };
  }
}

export function useDateRange(initial: RangePreset = "this_month") {
  const [preset, setPreset] = useState<RangePreset>(initial);
  const [range, setRange] = useState<DateRange>(() => presetRange(initial));

  const choosePreset = (p: RangePreset) => {
    setPreset(p);
    if (p !== "custom") setRange(presetRange(p));
  };

  return { preset, range, choosePreset, setRange };
}

const selectClass =
  "h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring";

const MAIN_PRESETS: RangePreset[] = ["today", "this_week", "this_month", "this_quarter", "this_year"];

/** Today / this week / month / quarter / year as buttons; yesterday, last week, last month, last 90 days and a custom range under "Other" */
export function DateRangeFilter({
  preset,
  range,
  onPreset,
  onRange,
}: {
  preset: RangePreset;
  range: DateRange;
  onPreset: (p: RangePreset) => void;
  onRange: (r: DateRange) => void;
}) {
  const other = !MAIN_PRESETS.includes(preset);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label="Period" className="flex max-w-full overflow-x-auto rounded-lg border border-border">
        {MAIN_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={preset === p}
            onClick={() => onPreset(p)}
            className={`h-[30px] shrink-0 whitespace-nowrap px-3 text-xs ${preset === p ? "bg-sidebar font-semibold text-white" : "bg-card text-muted-foreground hover:text-foreground"}`}
          >
            {PRESET_LABEL[p]}
          </button>
        ))}
        <SearchableSelect
          aria-label="Other periods"
          value={other ? preset : ""}
          onChange={(e) => e.target.value && onPreset(e.target.value as RangePreset)}
          className={`h-[30px] border-l border-border px-2 text-xs outline-none ${other ? "bg-sidebar font-semibold text-white" : "bg-card text-muted-foreground"}`}
        >
          <option value="">Other…</option>
          {(["yesterday", "last_week", "last_month", "last_90", "custom"] as RangePreset[]).map((p) => (
            <option key={p} value={p} className="text-foreground">
              {PRESET_LABEL[p]}
            </option>
          ))}
        </SearchableSelect>
      </div>
      {preset === "custom" && (
        <>
          <Input type="date" aria-label="From date" className="h-8 w-[140px] text-xs" value={range.from} max={range.to} onChange={(e) => e.target.value && onRange({ ...range, from: e.target.value })} />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" aria-label="To date" className="h-8 w-[140px] text-xs" value={range.to} min={range.from} onChange={(e) => e.target.value && onRange({ ...range, to: e.target.value })} />
        </>
      )}
    </div>
  );
}

export { selectClass as filterSelectClass };

/** Fetch a report endpoint whenever its URL changes */
export function useReport<T>(url: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    setError(null);
    try {
      setData(unwrapObject<T>(await api.get(url)));
    } catch (err: any) {
      setError(err.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

export function buildQuery(params: Record<string, string | number | undefined | null>) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.append(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
}
